import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageMeta from '../components/common/PageMeta';
import PageBreadcrumb from '../components/common/PageBreadCrumb';
import { useUserProfilesState } from '../hooks/useUserProfilesState';
import { Card, CardContent } from '../components/ui/card';
import { supabase } from '../integrations/supabase/client';

// Add sanitization utility function
const sanitizeText = (text: string): string => {
    if (!text) return '';
    // Create a temporary div element
    const temp = document.createElement('div');
    // Set its text content (which automatically escapes HTML)
    temp.textContent = text;
    // Return the sanitized string
    return temp.innerHTML;
};

// Define field constraints
const FIELD_CONSTRAINTS = {
    firstName: { max: 50 },
    lastName: { max: 50 },
    displayName: { max: 100 },
    bio: { max: 500 },
    location: { max: 100 },
    phoneNumber: { max: 20 },
    website: { max: 200 },
    company: { max: 100 },
    jobTitle: { max: 100 },
    interests: { maxItems: 10, maxLength: 30 }
};

// Interface for personal profile details
interface PersonalProfileDetails {
    firstName: string;
    lastName: string;
    displayName: string;
    bio: string;
    location: string;
    phoneNumber: string;
    website: string;
    company: string;
    jobTitle: string;
    interests: string[];
}

// Default values for the form
const defaultPersonalProfile: PersonalProfileDetails = {
    firstName: '',
    lastName: '',
    displayName: '',
    bio: '',
    location: '',
    phoneNumber: '',
    website: '',
    company: '',
    jobTitle: '',
    interests: []
};

export default function EditPersonalProfile() {
    const navigate = useNavigate();
    const { userProfile, isLoading, refreshUserProfile } = useUserProfilesState();

    // Form state
    const [formData, setFormData] = useState<PersonalProfileDetails>(defaultPersonalProfile);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notification, setNotification] = useState<{
        show: boolean;
        variant: 'success' | 'error';
        message: string;
    }>({ show: false, variant: 'success', message: '' });
    const [interestsInput, setInterestsInput] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    // Load user's personal profile data when component mounts
    useEffect(() => {
        if (userProfile && userProfile.details && userProfile.details.personal) {
            try {
                // If details.personal is a string, parse it, otherwise use the object directly
                const personalDetails = typeof userProfile.details.personal === 'string'
                    ? JSON.parse(userProfile.details.personal)
                    : userProfile.details.personal;

                // Sanitize loaded data
                const sanitizedData: PersonalProfileDetails = {
                    firstName: sanitizeText(personalDetails.firstName || ''),
                    lastName: sanitizeText(personalDetails.lastName || ''),
                    displayName: sanitizeText(personalDetails.displayName || ''),
                    bio: sanitizeText(personalDetails.bio || ''),
                    location: sanitizeText(personalDetails.location || ''),
                    phoneNumber: sanitizeText(personalDetails.phoneNumber || ''),
                    website: sanitizeText(personalDetails.website || ''),
                    company: sanitizeText(personalDetails.company || ''),
                    jobTitle: sanitizeText(personalDetails.jobTitle || ''),
                    interests: Array.isArray(personalDetails.interests) 
                        ? personalDetails.interests.map((item: string) => sanitizeText(item))
                        : []
                };

                setFormData(sanitizedData);

                // If interests exist, set the interests input
                if (sanitizedData.interests && Array.isArray(sanitizedData.interests)) {
                    setInterestsInput(sanitizedData.interests.join(', '));
                }
            } catch (err) {
                console.error('Error parsing personal profile details:', err);
                setError('Failed to load personal profile details');
            }
        }
    }, [userProfile]);

    // Validate a single field
    const validateField = (name: string, value: string | string[]): string => {
        // Check if field exists in constraints
        if (!FIELD_CONSTRAINTS.hasOwnProperty(name)) {
            return '';
        }

        // Handle interests array specially since its constraints are different
        if (name === 'interests' && Array.isArray(value)) {
            const constraints = FIELD_CONSTRAINTS.interests;
            if (value.length > constraints.maxItems) {
                return `Maximum ${constraints.maxItems} interests allowed`;
            }
            
            for (const item of value) {
                if (item.length > constraints.maxLength) {
                    return `Each interest can't exceed ${constraints.maxLength} characters`;
                }
            }
            return '';
        }

        // For text fields, check max length
        if (typeof value === 'string' && name !== 'interests') {
            const constraint = FIELD_CONSTRAINTS[name as keyof typeof FIELD_CONSTRAINTS];
            // Type guard to make sure we're dealing with an object that has 'max' property
            if ('max' in constraint) {
                if (value.length > constraint.max) {
                    return `Maximum ${constraint.max} characters allowed`;
                }
            }
        }
        
        return '';
    };

    // Handle text input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        
        // Sanitize the input value
        const sanitizedValue = sanitizeText(value);
        
        // Validate the field
        const error = validateField(name, sanitizedValue);
        
        // Update fieldErrors - if there's no error, remove the field from errors object
        setFieldErrors(prev => {
            const newErrors = { ...prev };
            if (error) {
                newErrors[name] = error;
            } else {
                delete newErrors[name];
            }
            return newErrors;
        });

        // Only update if within limits or empty
        if (!error || sanitizedValue === '') {
            setFormData(prev => ({
                ...prev,
                [name]: sanitizedValue
            }));
        }
    };

    // Handle interests input
    const handleInterestsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        setInterestsInput(inputValue);
        
        // Split by commas and trim whitespace
        const interestsArray = inputValue
            .split(',')
            .map(item => sanitizeText(item.trim()))
            .filter(item => item !== '');

        // Validate interests
        const error = validateField('interests', interestsArray);
        
        // Update fieldErrors - if there's no error, remove the field from errors object
        setFieldErrors(prev => {
            const newErrors = { ...prev };
            if (error) {
                newErrors.interests = error;
            } else {
                delete newErrors.interests;
            }
            return newErrors;
        });

        // Only update if within limits
        if (!error) {
            setFormData(prev => ({
                ...prev,
                interests: interestsArray
            }));
        }
    };

    // Validate entire form
    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};
        let isValid = true;

        // Validate each field
        for (const [key, value] of Object.entries(formData)) {
            const error = validateField(key, value);
            if (error) {
                errors[key] = error;
                isValid = false;
            }
        }

        // Basic validation
        /* if (!formData.firstName.trim()) {
            errors.firstName = 'First name is required';
            isValid = false;
        }
        if (!formData.lastName.trim()) {
            errors.lastName = 'Last name is required';
            isValid = false;
        } */

        setFieldErrors(errors);
        return isValid;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        
        // Validate form before submission
        if (!validateForm()) {
            return;
        }
        
        setIsSubmitting(true);
        setNotification({ show: false, variant: 'success', message: '' });

        try {
            // Set display name if not provided
            let updatedFormData = {...formData};
            if (!updatedFormData.displayName.trim()) {
                updatedFormData.displayName = `${updatedFormData.firstName} ${updatedFormData.lastName}`;
            }

            // Call Supabase function to update the personal profile
            const { data, error } = await supabase.functions.invoke('profile-personal', {
                method: 'PUT',
                body: updatedFormData
            });

            if (error) {
                console.log('error updatePersonalProfile error is', error);
                setNotification({
                    show: true,
                    variant: 'error',
                    message: sanitizeText(error.message) || 'Failed to update profile'
                });
            } else {
                // Show success notification
                setNotification({
                    show: true,
                    variant: 'success',
                    message: 'Personal profile updated successfully!'
                });
            }

            // Refresh the user profile to get updated data
            await refreshUserProfile();

            // Auto-hide notification after 3 seconds
            setTimeout(() => {
                setNotification(prev => ({ ...prev, show: false }));
            }, 3000);

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
            setError(sanitizeText(errorMessage));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <PageMeta
                title="Edit Personal Profile"
                description="Update your personal profile information"
            />
            <PageBreadcrumb pageTitle="Edit Personal Profile" />

            <div className="max-w-4xl mx-auto">
                <Card>
                    <CardContent className="p-6">
                        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white">
                            Personal Information
                        </h2>

                        {notification.show && (
                            <div className={`mb-6 p-4 rounded-md ${notification.variant === 'success'
                                    ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                                    : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
                                }`}>
                                {notification.message}
                            </div>
                        )}

                        {error && (
                            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
                                {error}
                            </div>
                        )}

                        {isLoading ? (
                            <div className="flex justify-center items-center py-8">
                                <div className="w-8 h-8 border-4 border-t-blue-500 border-b-gray-200 border-l-gray-200 border-r-gray-200 rounded-full animate-spin dark:border-b-gray-700 dark:border-l-gray-700 dark:border-r-gray-700"></div>
                                <span className="ml-2 text-gray-600 dark:text-gray-400">Loading profile data...</span>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Name Section */}
                                    <div>
                                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            First Name <span className="text-xs text-gray-500">({FIELD_CONSTRAINTS.firstName.max} chars max)</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="firstName"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            maxLength={FIELD_CONSTRAINTS.firstName.max}
                                            className={`w-full px-3 py-2 border ${fieldErrors.firstName ? 'border-red-500' : 'border-gray-300'} 
                                                rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                                                dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
                                            
                                        />
                                        {fieldErrors.firstName && (
                                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.firstName}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Last Name <span className="text-xs text-gray-500">({FIELD_CONSTRAINTS.lastName.max} chars max)</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="lastName"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            maxLength={FIELD_CONSTRAINTS.lastName.max}
                                            className={`w-full px-3 py-2 border ${fieldErrors.lastName ? 'border-red-500' : 'border-gray-300'} 
                                                rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                                                dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
                                            
                                        />
                                        {fieldErrors.lastName && (
                                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.lastName}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Display Name <span className="text-xs text-gray-500">({FIELD_CONSTRAINTS.displayName.max} chars max)</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="displayName"
                                            name="displayName"
                                            value={formData.displayName}
                                            onChange={handleInputChange}
                                            maxLength={FIELD_CONSTRAINTS.displayName.max}
                                            placeholder="How you want to be known on the platform"
                                            className={`w-full px-3 py-2 border ${fieldErrors.displayName ? 'border-red-500' : 'border-gray-300'} 
                                                rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                                                dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
                                        />
                                        {fieldErrors.displayName && (
                                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.displayName}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Phone Number <span className="text-xs text-gray-500">({FIELD_CONSTRAINTS.phoneNumber.max} chars max)</span>
                                        </label>
                                        <input
                                            type="tel"
                                            id="phoneNumber"
                                            name="phoneNumber"
                                            value={formData.phoneNumber}
                                            onChange={handleInputChange}
                                            maxLength={FIELD_CONSTRAINTS.phoneNumber.max}
                                            className={`w-full px-3 py-2 border ${fieldErrors.phoneNumber ? 'border-red-500' : 'border-gray-300'} 
                                                rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                                                dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
                                        />
                                        {fieldErrors.phoneNumber && (
                                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.phoneNumber}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Bio Section */}
                                <div>
                                    <label htmlFor="bio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Bio <span className="text-xs text-gray-500">({FIELD_CONSTRAINTS.bio.max} chars max)</span>
                                    </label>
                                    <textarea
                                        id="bio"
                                        name="bio"
                                        value={formData.bio}
                                        onChange={handleInputChange}
                                        rows={4}
                                        maxLength={FIELD_CONSTRAINTS.bio.max}
                                        placeholder="Tell us a bit about yourself"
                                        className={`w-full px-3 py-2 border ${fieldErrors.bio ? 'border-red-500' : 'border-gray-300'} 
                                            rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                                            dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
                                    />
                                    {fieldErrors.bio && (
                                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.bio}</p>
                                    )}
                                    <div className="mt-1 text-right text-xs text-gray-500">
                                        {formData.bio.length}/{FIELD_CONSTRAINTS.bio.max}
                                    </div>
                                </div>

                                {/* Location and Web Section */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Location <span className="text-xs text-gray-500">({FIELD_CONSTRAINTS.location.max} chars max)</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="location"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleInputChange}
                                            maxLength={FIELD_CONSTRAINTS.location.max}
                                            placeholder="City, Country"
                                            className={`w-full px-3 py-2 border ${fieldErrors.location ? 'border-red-500' : 'border-gray-300'} 
                                                rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                                                dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
                                        />
                                        {fieldErrors.location && (
                                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.location}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label htmlFor="website" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Website <span className="text-xs text-gray-500">({FIELD_CONSTRAINTS.website.max} chars max)</span>
                                        </label>
                                        <input
                                            type="url"
                                            id="website"
                                            name="website"
                                            value={formData.website}
                                            onChange={handleInputChange}
                                            maxLength={FIELD_CONSTRAINTS.website.max}
                                            placeholder="https://yourwebsite.com"
                                            className={`w-full px-3 py-2 border ${fieldErrors.website ? 'border-red-500' : 'border-gray-300'} 
                                                rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                                                dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
                                        />
                                        {fieldErrors.website && (
                                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.website}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Professional Info Section */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Company <span className="text-xs text-gray-500">({FIELD_CONSTRAINTS.company.max} chars max)</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="company"
                                            name="company"
                                            value={formData.company}
                                            onChange={handleInputChange}
                                            maxLength={FIELD_CONSTRAINTS.company.max}
                                            className={`w-full px-3 py-2 border ${fieldErrors.company ? 'border-red-500' : 'border-gray-300'} 
                                                rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                                                dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
                                        />
                                        {fieldErrors.company && (
                                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.company}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Job Title <span className="text-xs text-gray-500">({FIELD_CONSTRAINTS.jobTitle.max} chars max)</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="jobTitle"
                                            name="jobTitle"
                                            value={formData.jobTitle}
                                            onChange={handleInputChange}
                                            maxLength={FIELD_CONSTRAINTS.jobTitle.max}
                                            className={`w-full px-3 py-2 border ${fieldErrors.jobTitle ? 'border-red-500' : 'border-gray-300'} 
                                                rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                                                dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
                                        />
                                        {fieldErrors.jobTitle && (
                                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.jobTitle}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Interests Section */}
                                <div>
                                    <label htmlFor="interests" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Interests (comma-separated) <span className="text-xs text-gray-500">(max {FIELD_CONSTRAINTS.interests.maxItems} interests, {FIELD_CONSTRAINTS.interests.maxLength} chars each)</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="interests"
                                        name="interests"
                                        value={interestsInput}
                                        onChange={handleInterestsChange}
                                        placeholder="PDF, technology, design"
                                        className={`w-full px-3 py-2 border ${fieldErrors.interests ? 'border-red-500' : 'border-gray-300'} 
                                            rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                                            dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
                                    />
                                    {fieldErrors.interests && (
                                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.interests}</p>
                                    )}
                                    {formData.interests.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {formData.interests.map((interest, index) => (
                                                <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm dark:bg-blue-900/30 dark:text-blue-300">
                                                    {interest}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Form Actions */}
                                <div className="flex justify-end space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting || Object.keys(fieldErrors).length > 0}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 dark:disabled:bg-blue-800 dark:disabled:text-blue-100"
                                    >
                                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        </>
    );
} 